import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import CostCenterService from "../services/costCenter.service";
import { ICostCenterState } from "../types";
import { StoreState } from "@/store/type";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import { formatString } from "@/core/utils/helpers";
import analyticsService from "@/analytic.service";
import { COST_CENTER_EVENTS } from "../constants/analyticsEvents";
import {
  CreateCostCenterDto,
  UpdateCostCenterDto,
} from "../dtos/costCenter.dtos";

const costCenterService = new CostCenterService();

const actions: ActionTree<ICostCenterState, StoreState> = {
  async fetchCostCenters(
    context: ActionContext<ICostCenterState, StoreState>,
    payload: { organizationId: string; isActive?: boolean },
  ) {
    try {
      context.commit("setFetchingCostCenters", true);
      const { organizationId, isActive } = payload;
      const response: ICoreResponse = await costCenterService.getCostCenters(
        organizationId,
        isActive,
      );
      if (response.success && response.status === StatusCodes.OK) {
        context.commit("setCostCenters", response.data);
        analyticsService.logActionEvent(COST_CENTER_EVENTS.COST_CENTER_FETCH, {
          organizationId,
          isActive,
        });
      } else {
        throw response;
      }
    } catch (error: unknown) {
      context.commit("setCostCenters", []);
    } finally {
      context.commit("setFetchingCostCenters", false);
    }
  },

  async createCostCenter(
    context: ActionContext<ICostCenterState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: CreateCostCenterDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setCostCenterActionLoading", true);
      const response: ICoreResponse = await costCenterService.createCostCenter(
        organizationId,
        body,
      );
      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler();
        analyticsService.logActionEvent(COST_CENTER_EVENTS.COST_CENTER_CREATE, {
          organizationId,
          name: body.name,
        });
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || NOTIFICATION_MESSAGES.COST_CENTER_CREATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setCostCenterActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async updateCostCenter(
    context: ActionContext<ICostCenterState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: UpdateCostCenterDto;
      successHandler: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setCostCenterActionLoading", true);
      const response: ICoreResponse = await costCenterService.updateCostCenter(
        organizationId,
        body,
      );
      if (response.success && response.status === StatusCodes.OK) {
        successHandler();
        analyticsService.logActionEvent(COST_CENTER_EVENTS.COST_CENTER_UPDATE, {
          organizationId,
          publicId: body.public_id,
          name: body.name,
        });
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || NOTIFICATION_MESSAGES.COST_CENTER_UPDATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setCostCenterActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteCostCenter(
    context: ActionContext<ICostCenterState, StoreState>,
    {
      organizationId,
      publicId,
      successHandler,
    }: {
      organizationId: string;
      publicId: string;
      successHandler: () => void;
    },
  ) {
    try {
      context.commit("setCostCenterActionLoading", true);
      const response: ICoreResponse = await costCenterService.deleteCostCenter(
        organizationId,
        publicId,
      );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedCostCenters = context.state.costCenters.filter(
          (center) => center.public_id !== publicId,
        );
        context.commit("setCostCenters", updatedCostCenters);
        successHandler();
        analyticsService.logActionEvent(COST_CENTER_EVENTS.COST_CENTER_DELETE, {
          organizationId,
          publicId,
        });
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message || NOTIFICATION_MESSAGES.COST_CENTER_DELETE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setCostCenterActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async patchCostCenterStatus(
    context: ActionContext<ICostCenterState, StoreState>,
    {
      organizationId,
      costCenterId,
      costCenterName,
      isActive,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      costCenterId: string;
      costCenterName: string;
      isActive: boolean;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      context.commit("setCostCenterActionLoading", true);
      const response: ICoreResponse =
        await costCenterService.patchCostCenterStatus(
          organizationId,
          costCenterId,
          isActive,
        );
      if (response.success && response.status === StatusCodes.OK) {
        const updatedCostCenters = context.state.costCenters.map((center) => {
          if (center.public_id === costCenterId) {
            return { ...center, is_active: isActive };
          }
          return center;
        });
        context.commit("setCostCenters", updatedCostCenters);
        if (successHandler) {
          successHandler();
        }
        analyticsService.logActionEvent(
          COST_CENTER_EVENTS.COST_CENTER_STATUS_TOGGLE,
          { organizationId, costCenterId, costCenterName, isActive },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.COST_CENTER_STATUS_UPDATE_SUCCESS,
          costCenterName,
          isActive ? "active" : "inactive",
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      if (errorHandler) {
        errorHandler();
      }
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setCostCenterActionLoading", false);
      notificationService.triggerNotification();
    }
  },
};

export default actions;
